import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Vector3 } from 'three'
import styled from 'styled-components'
import gsap from 'gsap'
import { Utils } from 'streamr-client-protocol'
import feed from './utils/feed'
import { getReversedGeocodedLocation } from './utils/geolocation'
import Tooltip from './Tooltip'
import NetworkVisualizer from '.'

const UnstyledNetworkExplorer = ({
    className,
    offsetFn,
    onIntroDone,
    onLoaded,
    onNodeChange,
    setLoading,
}) => {
    const setLoadingRef = useRef()
    setLoadingRef.current = setLoading

    const [tooltipValue, setTooltipValue] = useState({
        id: undefined,
        title: '',
        address: '',
        location: '',
        url: '',
        streamId: undefined,
    })
    const [locations, setLocations] = useState({})
    const locationsRef = useRef(locations)
    locationsRef.current = locations

    const offsetFnRef = useRef(offsetFn)
    offsetFnRef.current = offsetFn

    const canvasRef = useRef()

    const tooltipRef = useRef()

    const rootRef = useRef()

    const mountedRef = useRef(false)

    const onNodeChangeRef = useRef()
    onNodeChangeRef.current = onNodeChange

    const fetchLocation = useCallback(async ({ id, latitude, longitude }) => {
        if (!locationsRef.current || !!locationsRef.current[id]) {
            return
        }

        const { region } = await getReversedGeocodedLocation({
            latitude,
            longitude,
        })

        if (region) {
            setLocations((prevLocations) => ({
                ...prevLocations,
                [id]: region,
            }))
        }
    }, [])

    useEffect(() => {
        const { current: tooltip } = tooltipRef

        if (!tooltip || tooltipValue.title === '') {
            tooltip.style.visibility = 'hidden'
            return
        }

        tooltip.style.visibility = 'visible'

        gsap.killTweensOf(tooltip.style)

        gsap.to(tooltip.style, {
            duration: 0.25,
            ease: 'quad.out',
            opacity: 1,
        })
    }, [tooltipValue])

    const onIntroDoneRef = useRef()

    onIntroDoneRef.current = onIntroDone

    useEffect(() => {
        const { current: canvas } = canvasRef

        let selectedPoint = null

        let prevBlur = 0

        let size = {
            width: 2,
            height: 2,
        }

        mountedRef.current = true

        const onHidden = (newPoint, metadata) => {
            const { current: tooltip } = tooltipRef

            if (!tooltip) {
                return
            }

            if (typeof onNodeChangeRef.current === 'function') {
                onNodeChangeRef.current(newPoint)
            }

            selectedPoint = newPoint

            if (!newPoint) {
                setTooltipValue({
                    id: undefined,
                    title: '',
                    address: '',
                    location: '',
                    streamId: undefined,
                })
                return
            }

            const {
                nodeId: id,
                longitude,
                latitude,
                country,
                streamId,
            } = metadata

            const address = Utils.parseAddressFromNodeId(id)

            const title = (() => {
                try {
                    return Utils.generateMnemonicFromAddress(address)
                } catch (e) {
                    return address
                }
            })()

            setTooltipValue({
                id,
                title,
                address,
                location: country,
                streamId,
            })

            fetchLocation({
                id,
                latitude,
                longitude,
            })
        }

        const onSelect = (point, { showTooltip = true, metadata }) => {
            const { current: tooltip } = tooltipRef

            if (!tooltip || !showTooltip) {
                return
            }

            gsap.killTweensOf(tooltip.style)

            gsap.to(tooltip.style, {
                duration: tooltip.style.opacity * 0.25,
                ease: 'quad.in',
                opacity: 0,
                onComplete: () => {
                    onHidden(point, metadata)
                },
            })
        }

        const onPan = (camera) => {
            const { width, height } = size

            const { current: tooltip } = tooltipRef

            if (!tooltip || !selectedPoint) {
                return
            }

            const vector = new Vector3(selectedPoint.x, 0, selectedPoint.z)

            vector.project(camera)

            vector.x = ((vector.x + 1) * width) / 2

            vector.y = ((-vector.y + 1) * height) / 2

            const pos = Math.min(1.0, Math.abs((vector.y / height - 0.5) * 2.0))

            tooltip.style.transform = `translate(${vector.x}px, ${vector.y - 10 * (1 - tooltip.style.opacity)}px)`

            const blur = Math.round(pos * pos * pos * 4)

            if (blur !== prevBlur) {
                const filter = `blur(${blur}px)`
                tooltip.style.webkitFilter = filter
                tooltip.style.mozFilter = filter
                tooltip.style.oFilter = filter
                tooltip.style.msFilter = filter
                tooltip.style.filter = filter
                prevBlur = blur
            }
        }

        if (!canvas) {
            return () => {}
        }

        const vis = new NetworkVisualizer(canvas, {
            offsetFn: offsetFnRef.current,
            setLoading: (value) => {
                if (typeof setLoadingRef.current === 'function') {
                    setLoadingRef.current(value)
                }
            },
            onIntroDone: onIntroDoneRef.current,
            onPan,
            onSelect,
        })

        const onResize = () => {
            const { current: root } = rootRef

            if (!root) {
                return
            }

            size = {
                height: root.offsetHeight,
                width: root.offsetWidth,
            }

            vis.resize(size)
        }

        window.addEventListener('resize', onResize)

        const onComplete = () => {
            if (!mountedRef.current) {
                return
            }

            onResize()

            vis.start()

            feed()
                .then((data) => {
                    if (mountedRef.current) {
                        vis.feed(data)
                    }

                    if (typeof onLoaded === 'function') {
                        onLoaded(data)
                    }

                    return null
                })
                .catch((e) => {
                    // eslint-disable-next-line no-console
                    console.log('Could not load nodes.', e)

                    if (typeof onLoaded === 'function') {
                        onLoaded()
                    }
                })
        }

        vis.load(onComplete)

        return () => {
            window.removeEventListener('resize', onResize)

            mountedRef.current = false

            vis.stop()

            const { current: tooltip } = tooltipRef

            if (tooltip) {
                gsap.killTweensOf(tooltip.style)
            }
        }
    }, [fetchLocation, onLoaded])

    return (
        <div className={className} ref={rootRef}>
            <Tooltip
                innerRef={tooltipRef}
                {...tooltipValue}
                location={(locations && locations[tooltipValue.id]) ? locations[tooltipValue.id] : tooltipValue.location}
            />
            <canvas ref={canvasRef} />
        </div>
    )
}

const NetworkExplorer = styled(UnstyledNetworkExplorer)`
    height: 100%;
    overflow: hidden;
    user-select: none;
    width: 100%;

    canvas {
        height: 100%;
        width: 100%;
    }
`

export default NetworkExplorer
